/* eslint-disable react/require-default-props */
import React, { useState } from 'react';
import { useAudioRecorderController } from 'hooks/useAudioRecorderController';
import { speechToText } from 'services/actions/chatbot_actions';
import createTrack from 'services/actions/tracker_actions';
import { AudioWaveRecorder } from '../audio_wave';

interface ChatbotInputProps {
  componentRef: React.MutableRefObject<HTMLInputElement>;
  isLoading: boolean;
  sendMessage: () => void;
  source?: string;
  variant?: 'dark' | 'white';
  footerFeedbackAction?: React.ReactNode;
}

const ChatbotInput = (props: ChatbotInputProps) => {
  const {
    componentRef,
    isLoading,
    sendMessage,
    source,
    variant = 'white',
    footerFeedbackAction,
  } = props;
  const { waveRef, isRecording, startRecording, cancelRecording, stopRecording } =
    useAudioRecorderController();
  const [transcribing, setTranscribing] = useState(false);

  const isAudioBusy = isRecording || transcribing;

  const handleFinish = async (blob: Blob) => {
    const formData = new FormData();
    formData.append('audio', blob, 'audio.webm');

    try {
      setTranscribing(true);
      createTrack(`${source} - Ditar`);

      const response = await speechToText(formData);

      if (response.status === 200 && componentRef.current) {
        const newText = response.data?.text?.trim();
        if (!newText) return;

        const currentText = componentRef.current.innerText?.trim();

        componentRef.current.innerText = currentText ? `${currentText} ${newText}` : newText;

        componentRef.current.focus();
      }
    } catch (error) {
      console.error('Erro na transcrição:', error);
    } finally {
      setTranscribing(false);
    }
  };

  return (
    <div className="chatbot-form p-3">
      <div className="chatbot-input-group">
        <div
          aria-label="Digite uma mensagem"
          ref={componentRef}
          role="textbox"
          tabIndex={0}
          contentEditable={!isLoading && !isAudioBusy}
          className={`chatbot-input my-1 ${isRecording ? 'd-none' : ''}`}
          onPaste={(e) => {
            e.preventDefault();
            const text = e.clipboardData.getData('text/plain');
            document.getSelection()?.getRangeAt(0)?.insertNode(document.createTextNode(text));
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              sendMessage();
            }
          }}
        />
        {isRecording && <AudioWaveRecorder ref={waveRef} variant={variant} />}
        <div className="chatbot-audio-control mr-1">
          {isAudioBusy && (
            <>
              <button
                type="button"
                className="btn d-flex align-items-center p-0 chatbot-audio-cancel"
                onClick={cancelRecording}
                disabled={transcribing}
              >
                <span className="material-icons">close</span>
              </button>
              <button
                type="button"
                className="btn d-flex align-items-center p-0 chatbot-audio-submit ml-1"
                onClick={() => stopRecording(handleFinish)}
                disabled={transcribing}
              >
                {transcribing ? (
                  <div className="chatbot-audio-equalizer-bars">
                    <span />
                    <span />
                    <span />
                  </div>
                ) : (
                  <span className="material-icons">check</span>
                )}
              </button>
            </>
          )}
          {!isAudioBusy && (
            <button
              type="button"
              className="btn btn-chatbot-audio d-flex align-items-center p-0"
              onClick={startRecording}
            >
              <span className="material-icons">mic</span>
            </button>
          )}
        </div>
        <button
          type="button"
          className="btn btn-chatbot-send d-flex align-items-center"
          disabled={isLoading || isAudioBusy}
          onClick={() => sendMessage()}
        >
          <span className="material-icons">send</span>
        </button>
      </div>
      {footerFeedbackAction ? (
        <div className="chatbot-footer-meta w-100 text-center">
          <div className="chatbot-footer-meta-inner d-inline-flex flex-wrap justify-content-center align-items-center">
            <span className="chatbot-notice mb-0">
              Esse chat pode cometer erros. Verifique as informações importantes.
            </span>
            <span className="chatbot-footer-feedback">{footerFeedbackAction}</span>
          </div>
        </div>
      ) : (
        <div className="chatbot-notice">
          Esse chat pode cometer erros. Verifique as informações importantes.
        </div>
      )}
    </div>
  );
};

export default ChatbotInput;
